import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Alert, Button, Col, Row, Table } from 'react-bootstrap';
import { CartesianGrid, Area, AreaChart, Line, LineChart, Tooltip, XAxis, YAxis } from 'recharts';
import { getActivity, getActivityLaps, getActivitySamples } from '../api/trainer';
import { Activity, ActivityAdjustment } from '../api/types';
import AdjustmentModal from '../components/AdjustmentModal';
import ChartCard from '../components/ChartCard';
import PageHeader from '../components/PageHeader';
import StatusMessage from '../components/StatusMessage';
import { useAsync } from '../hooks/useAsync';
import { Y_AXIS, yAxisProps } from '../utils/axis';
import {
  activityTypeLabel,
  formatDate,
  formatDistance,
  formatDuration,
  formatNumber,
  formatPace,
} from '../utils/format';

/** Stored samples from standing still have a huge pace (s/km); beyond this they are left out of the chart. */
const MAX_CHART_PACE = 1000;

interface Detail {
  label: string;
  value: string;
}

/** The detail numbers Garmin reported for an activity. A number it did not report is left out, not shown as zero. */
function detailsOf(a: Activity): Detail[] {
  const has = (v: number | null | undefined): v is number => v != null && Number.isFinite(v);
  const out: Detail[] = [];
  if (has(a.movingS)) out.push({ label: 'Moving time', value: formatDuration(a.movingS) });
  if (has(a.calories)) out.push({ label: 'Calories', value: `${formatNumber(a.calories)} kcal` });
  if (has(a.elevationGainM)) {
    out.push({
      label: 'Elevation',
      value: `+${formatNumber(a.elevationGainM)} m${has(a.elevationLossM) ? ` / −${formatNumber(a.elevationLossM)} m` : ''}`,
    });
  }
  if (has(a.maxSpeedMps) && a.maxSpeedMps > 0) out.push({ label: 'Best pace', value: formatPace(1000 / a.maxSpeedMps) });
  if (has(a.avgCadence)) out.push({ label: 'Cadence', value: `${formatNumber(a.avgCadence)} spm` });
  if (has(a.steps)) out.push({ label: 'Steps', value: formatNumber(a.steps) });
  if (has(a.avgStrideLengthCm)) out.push({ label: 'Stride length', value: `${formatNumber(a.avgStrideLengthCm, 0)} cm` });
  if (has(a.avgGroundContactMs)) out.push({ label: 'Ground contact', value: `${formatNumber(a.avgGroundContactMs)} ms` });
  if (has(a.avgVerticalOscillationCm)) out.push({ label: 'Vertical oscillation', value: `${formatNumber(a.avgVerticalOscillationCm, 1)} cm` });
  if (has(a.avgVerticalRatio)) out.push({ label: 'Vertical ratio', value: `${formatNumber(a.avgVerticalRatio, 1)} %` });
  if (has(a.avgPowerW)) out.push({ label: 'Power', value: `${formatNumber(a.avgPowerW)} W` });
  if (has(a.aerobicTrainingEffect)) out.push({ label: 'Aerobic effect', value: formatNumber(a.aerobicTrainingEffect, 1) });
  if (has(a.anaerobicTrainingEffect)) out.push({ label: 'Anaerobic effect', value: formatNumber(a.anaerobicTrainingEffect, 1) });
  if (has(a.vo2Max)) out.push({ label: 'VO2max estimate', value: formatNumber(a.vo2Max, 1) });
  if (has(a.lactateThresholdBpm)) out.push({ label: 'Lactate threshold HR', value: `${formatNumber(a.lactateThresholdBpm)} bpm` });
  if (has(a.minTempC) || has(a.maxTempC)) {
    out.push({
      label: 'Temperature',
      value: `${has(a.minTempC) ? formatNumber(a.minTempC, 0) : '?'} to ${has(a.maxTempC) ? formatNumber(a.maxTempC, 0) : '?'} °C`,
    });
  }
  return out;
}

const ActivityDetails: React.FC<{ activity: Activity }> = ({ activity }) => {
  const details = detailsOf(activity);
  if (details.length === 0) return null;
  return (
    <section className="surface mb-3" aria-label="Activity details">
      <h2>Details{activity.personalRecord ? ' · personal record' : ''}</h2>
      <Row className="g-3">
        {details.map(d => (
          <Col key={d.label} xs={6} md={3}>
            <div className="stat-label">{d.label}</div>
            <div className="tabular fw-semibold">{d.value}</div>
          </Col>
        ))}
      </Row>
    </section>
  );
};
const AXIS = { stroke: 'var(--text-muted)', fontSize: 12 };

const ActivityDetailPage: React.FC = () => {
  const { id } = useParams();
  const numericId = Number(id);
  const valid = Number.isInteger(numericId) && numericId > 0;

  const invalidId = () => Promise.reject(new Error('Invalid activity id'));
  const activity = useAsync(() => (valid ? getActivity(numericId) : invalidId()), [numericId]);
  const laps = useAsync(() => (valid ? getActivityLaps(numericId) : invalidId()), [numericId]);
  const samples = useAsync(() => (valid ? getActivitySamples(numericId) : invalidId()), [numericId]);

  const [showAdjustment, setShowAdjustment] = useState(false);
  // The API has no read endpoint for an adjustment, so this only holds what was saved in this visit.
  const [savedAdjustment, setSavedAdjustment] = useState<ActivityAdjustment | null>(null);

  if (!valid) {
    return <StatusMessage loading={false} error="That activity link is not valid." />;
  }

  const a = activity.data;
  const series = (samples.data ?? []).map(s => ({
    minute: Number((s.timeS / 60).toFixed(1)),
    pace: s.paceSPerKm != null && s.paceSPerKm <= MAX_CHART_PACE ? s.paceSPerKm : null,
    heartRate: s.hr,
    altitude: s.altitudeM,
  }));
  const hasSamples = series.length > 0;

  return (
    <>
      <PageHeader title={a?.name ?? 'Activity'}>
        <p>
          <Link to="/activities">← All activities</Link>
          {a && (
            <>
              {' · '}
              {activityTypeLabel(a.activityType)} · {formatDate(a.startTime)}
            </>
          )}
        </p>
      </PageHeader>

      <StatusMessage loading={activity.loading} error={activity.error} onRetry={activity.reload} />

      {a && (
        <>
          <Row className="g-3 mb-3">
            <Col xs={6} md={3}>
              <section className="surface">
                <div className="stat-label">Distance</div>
                <div className="stat-value tabular">{formatDistance(a.distanceM)}</div>
              </section>
            </Col>
            <Col xs={6} md={3}>
              <section className="surface">
                <div className="stat-label">Time</div>
                <div className="stat-value tabular">{formatDuration(a.durationS)}</div>
              </section>
            </Col>
            <Col xs={6} md={3}>
              <section className="surface">
                <div className="stat-label">Avg pace</div>
                <div className="stat-value tabular">{formatPace(a.avgPaceSPerKm)}</div>
              </section>
            </Col>
            <Col xs={6} md={3}>
              <section className="surface">
                <div className="stat-label">Heart rate</div>
                <div className="stat-value tabular">{formatNumber(a.avgHr)} bpm</div>
                <div className="stat-hint">max {formatNumber(a.maxHr)}</div>
              </section>
            </Col>
          </Row>

          <ActivityDetails activity={a} />

          <section className="surface mb-3 d-flex flex-wrap justify-content-between align-items-center gap-2">
            <div>
              <h2 className="mb-1">Official result</h2>
              {savedAdjustment ? (
                <p className="muted small mb-0">
                  Saved: {formatDistance(savedAdjustment.officialDistanceM)} in {formatDuration(savedAdjustment.officialTimeS)}
                  {savedAdjustment.note ? ` · ${savedAdjustment.note}` : ''}
                </p>
              ) : (
                <p className="muted small mb-0">
                  Use this when the run was tracked a bit long or short. The records then use your official time.
                </p>
              )}
            </div>
            <Button variant="outline-primary" onClick={() => setShowAdjustment(true)}>
              Set official time
            </Button>
          </section>
          <AdjustmentModal
            activityId={numericId}
            trackedDistanceM={a.distanceM}
            show={showAdjustment}
            onClose={() => setShowAdjustment(false)}
            onChanged={setSavedAdjustment}
          />

          {!a.fitDownloaded && (
            <Alert variant="info" className="py-2">
              Laps and the per-second charts come from the FIT file, which has not been downloaded for this activity yet.
            </Alert>
          )}

          <Row className="g-3 mb-3">
            <Col md={6}>
              <ChartCard title="Pace (min/km, lower is faster)" empty={!samples.loading && !hasSamples}>
                <LineChart data={series} margin={{ top: 8, right: 8, bottom: 0, left: -8 }}>
                  <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
                  <XAxis dataKey="minute" {...AXIS} tickFormatter={v => `${v}m`} />
                  <YAxis {...Y_AXIS} {...yAxisProps(series, ['pace'], { kind: 'time' })} reversed width={48} tickFormatter={v => formatDuration(Math.round(v))} />
                  <Tooltip />
                  <Line type="monotone" dataKey="pace" stroke="var(--chart-2)" dot={false} strokeWidth={2} connectNulls />
                </LineChart>
              </ChartCard>
            </Col>
            <Col md={6}>
              <ChartCard title="Heart rate (bpm)" empty={!samples.loading && !hasSamples}>
                <LineChart data={series} margin={{ top: 8, right: 8, bottom: 0, left: -8 }}>
                  <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
                  <XAxis dataKey="minute" {...AXIS} tickFormatter={v => `${v}m`} />
                  <YAxis {...Y_AXIS} {...yAxisProps(series, ['heartRate'])} />
                  <Tooltip />
                  <Line type="monotone" dataKey="heartRate" stroke="var(--chart-4)" dot={false} strokeWidth={2} />
                </LineChart>
              </ChartCard>
            </Col>
            <Col xs={12}>
              <ChartCard title="Elevation (m)" empty={!samples.loading && !hasSamples}>
                <AreaChart data={series} margin={{ top: 8, right: 8, bottom: 0, left: -8 }}>
                  <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
                  <XAxis dataKey="minute" {...AXIS} tickFormatter={v => `${v}m`} />
                  <YAxis {...Y_AXIS} {...yAxisProps(series, ['altitude'])} />
                  <Tooltip />
                  <Area type="monotone" dataKey="altitude" stroke="var(--chart-1)" fill="var(--chart-1)" fillOpacity={0.2} />
                </AreaChart>
              </ChartCard>
            </Col>
          </Row>
        </>
      )}
      <StatusMessage loading={samples.loading} error={samples.error} onRetry={samples.reload} />

      <section className="surface">
        <h2>Laps</h2>
        <StatusMessage loading={laps.loading} error={laps.error} onRetry={laps.reload} />
        {laps.data && laps.data.length === 0 && <p className="muted mb-0">No laps recorded.</p>}
        {laps.data && laps.data.length > 0 && (
          <div className="table-responsive">
            <Table size="sm" className="trainer-table">
              <thead>
                <tr>
                  <th>Lap</th>
                  <th className="text-end">Distance</th>
                  <th className="text-end">Time</th>
                  <th className="text-end">Pace</th>
                  <th className="text-end">Avg HR</th>
                  <th className="text-end">Ascent</th>
                </tr>
              </thead>
              <tbody>
                {laps.data.map(lap => (
                  <tr key={lap.index}>
                    <td>{lap.index}</td>
                    <td className="text-end tabular">{formatDistance(lap.distanceM)}</td>
                    <td className="text-end tabular">{formatDuration(lap.durationS)}</td>
                    <td className="text-end tabular">{formatPace(lap.avgPaceSPerKm)}</td>
                    <td className="text-end tabular">{formatNumber(lap.avgHr)}</td>
                    <td className="text-end tabular">{formatNumber(lap.ascentM)} m</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </section>
    </>
  );
};

export default ActivityDetailPage;
